import { CertificacaoProgress, ColaboradorComProgress, GuRanking } from '../types';
import { TRILHAS, trilhaPoints } from './trilhas';

export function calcIndividualPoints(progress: CertificacaoProgress): number {
  let standard = 0;
  let anthropicEarned = false;
  for (const trilha of TRILHAS) {
    const pct = progress.trilhaProgress[trilha.id] ?? 0;
    const pts = trilhaPoints(trilha, pct / 100);
    if (trilha.tier === 'anthropic') {
      if (pts > 0) anthropicEarned = true;
    } else {
      standard += pts;
    }
  }
  return standard + (anthropicEarned ? 1000 : 0);
}

/**
 * Regulamento Fase Pocket §7.1 trata "certificado" como quem concluiu 100% +
 * certificação em qualquer trilha. Não restringe a trilhas elegíveis ao
 * prêmio — essa restrição (`countsForPrizeEligibility`) se aplica apenas à
 * elegibilidade de premiação em dinheiro (§7.2).
 */
export function getStatus(
  progress: CertificacaoProgress,
): 'certified' | 'in_progress' | 'not_started' {
  const certified = TRILHAS.some((t) => (progress.trilhaProgress[t.id] ?? 0) >= 100);
  if (certified) return 'certified';
  const anyProgress = TRILHAS.some((t) => (progress.trilhaProgress[t.id] ?? 0) > 0);
  return anyProgress ? 'in_progress' : 'not_started';
}

function isPrizeEligible(m: ColaboradorComProgress): boolean {
  // prizeCertifiedAt é preenchido em `universityApi` apenas para trilhas
  // com `countsForPrizeEligibility: true` (regulamento Fase Pocket §7.2).
  // `certifiedAt` (qualquer trilha) NÃO é usado aqui — Basics/Anthropic não
  // contam para elegibilidade ao prêmio.
  return m.progress.prizeCertifiedAt != null;
}

/**
 * Momento em que a GU "atingiu sua pontuação atual" — aproximado pelo evento
 * de certificação mais recente entre os membros. Quanto MENOR, mais cedo a GU
 * fechou a soma de pontos. Usado como segundo critério de desempate
 * (Fase Pocket §6.2).
 *
 * Membros sem certifiedAt são ignorados; se nenhum existir, retorna +Infinity
 * (a GU "ainda não atingiu" — perde no desempate).
 */
function maxCompletionMs(members: ColaboradorComProgress[]): number {
  let max = -Infinity;
  for (const m of members) {
    if (m.status !== 'certified' || !m.progress.certifiedAt) continue;
    const t = new Date(m.progress.certifiedAt).getTime();
    if (t > max) max = t;
  }
  return max === -Infinity ? Infinity : max;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function countPartialPointTrilhas(members: ColaboradorComProgress[]): number {
  let n = 0;
  for (const m of members) {
    for (const trilha of TRILHAS) {
      if (trilha.tier !== 'standard') continue;
      const pct = m.progress.trilhaProgress[trilha.id] ?? 0;
      if (pct >= 70 && pct < 100) n += 1;
    }
  }
  return n;
}

/**
 * Fase Pocket §6.2 (docs/regulamento/fase-pocket.md):
 *   `Pontos GU = round2( (Total de Pontos Individuais / elegiveis) × 100 × log10(elegiveis) )`
 *
 * O fator `log10(elegiveis)` favorece GUs maiores sem tornar o tamanho
 * determinístico — uma GU de 1 membro sempre pontua 0 porque log10(1) = 0.
 *
 * Desempate (Fase Pocket §6.2 + FAQ Premiação #12, PT/EN):
 *   1. Maior nº de pessoas certificadas (≥1 trilha a 100%).
 *   2. Ordem cronológica — primeira GU a atingir a pontuação, aproximado
 *      pela MENOR data de `max(certifiedAt)` entre os membros (o último
 *      evento contribuinte mais cedo).
 *
 * Nota: o regulamento ORIGINAL da Fase 1 (`AGENTIC LEAGUE - REGULAMENTO
 * OFICIAL.md` §7.3) tinha um 3º critério — "menor tempo médio de
 * certificação" — que a Fase Pocket removeu. Implementamos só os 2
 * critérios da Fase Pocket, que é o regulamento vigente.
 */
/**
 * Comparator individual — o regulamento só define desempate para GU
 * (Fase Pocket §6.2); este comparator é uma extensão análoga para pessoas,
 * não uma regra do regulamento:
 *   1. `totalPoints` desc.
 *   2. Nº de trilhas concluídas (≥100%) desc — análogo a "GU com mais
 *      certificadas".
 *   3. Data em que a pessoa bateu a contagem empatada no critério 2, asc
 *      (mais cedo vence) — usa o MAIOR `trilhaCertifiedAt` entre as trilhas
 *      a 100%, e não `certifiedAt` (que é a data da PRIMEIRA trilha). Usar a
 *      primeira trilha premiaria quem começou mais cedo, não quem atingiu a
 *      mesma quantidade de trilhas primeiro. Sem trilha concluída → sentinel
 *      +Infinity (análogo ao `maxCompletionMs` do GU).
 *   4. Nome asc (`localeCompare`) — fallback estável.
 */
export function compareIndividuals(
  a: ColaboradorComProgress,
  b: ColaboradorComProgress,
): number {
  if (b.totalPoints !== a.totalPoints) return b.totalPoints - a.totalPoints;
  const trilhasA = countTrilhasCompleted(a);
  const trilhasB = countTrilhasCompleted(b);
  if (trilhasB !== trilhasA) return trilhasB - trilhasA;
  const tA = reachedTrilhaCountAtMs(a);
  const tB = reachedTrilhaCountAtMs(b);
  if (tA !== tB) return tA - tB;
  return a.name.localeCompare(b.name);
}

function countTrilhasCompleted(m: ColaboradorComProgress): number {
  let n = 0;
  for (const trilha of TRILHAS) {
    if ((m.progress.trilhaProgress[trilha.id] ?? 0) >= 100) n += 1;
  }
  return n;
}

/**
 * Total de certificações emitidas (soma de trilhas a 100% de todas as
 * pessoas) — distinto de "pessoas certificadas", que conta pessoas, não
 * trilhas. Usado no KPI "Total de certificados" da página inicial.
 */
export function countTotalCertifications(members: ColaboradorComProgress[]): number {
  return members.reduce((sum, m) => sum + countTrilhasCompleted(m), 0);
}

function reachedTrilhaCountAtMs(m: ColaboradorComProgress): number {
  let max = -Infinity;
  for (const dateStr of Object.values(m.progress.trilhaCertifiedAt)) {
    if (!dateStr) continue;
    const t = new Date(dateStr).getTime();
    if (t > max) max = t;
  }
  return max === -Infinity ? Infinity : max;
}

export function buildGuRankings(members: ColaboradorComProgress[]): GuRanking[] {
  const groups = new Map<string, ColaboradorComProgress[]>();
  for (const m of members) {
    if (m.grupoGame === 'Não participa') continue;
    const arr = groups.get(m.grupoGame) ?? [];
    arr.push(m);
    groups.set(m.grupoGame, arr);
  }

  const rankings: Omit<GuRanking, 'position'>[] = [];
  for (const [grupoGame, gmembers] of groups.entries()) {
    const elegiveis = gmembers.length;
    const certified = gmembers.filter((m) => m.status === 'certified');
    const certificados = certified.length;
    const prizeEligibleCount = gmembers.filter(isPrizeEligible).length;
    const individualPoints = gmembers.reduce((sum, m) => sum + m.totalPoints, 0);
    const pontosGU =
      elegiveis > 0
        ? round2((individualPoints / elegiveis) * 100 * Math.log10(elegiveis))
        : 0;
    const pctCertificacao = elegiveis > 0 ? round2((certificados / elegiveis) * 100) : 0;
    rankings.push({
      grupoGame,
      totalEligiveis: elegiveis,
      certificados,
      prizeEligibleCount,
      pctCertificacao,
      partialPointTrilhas: countPartialPointTrilhas(gmembers),
      pontosGU,
      members: gmembers,
    });
  }

  rankings.sort((a, b) => {
    if (b.pontosGU !== a.pontosGU) return b.pontosGU - a.pontosGU;
    if (b.certificados !== a.certificados) return b.certificados - a.certificados;
    return maxCompletionMs(a.members) - maxCompletionMs(b.members);
  });

  return rankings.map((r, i) => ({
    ...r,
    position: i + 1,
  }));
}
